import React, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import classNames from 'classnames/bind'
import { Swiper, SwiperSlide } from 'swiper/react'
import { isEmpty, isNull, isUndefined } from 'lodash-es'
import { useTranslation } from 'next-i18next'

// Libs
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'
import useMedia from '@/hooks/useMedia'
import useSearchData from '@/hooks/useSearchData'
import useSearchHotKey from '@/hooks/useSearchHotKey'
import { handleGtmClickHost, handleGtmClickMerchant } from '@/methods/handleGtmEvent'

// Components
import ColorfulTab from '@/components/shared/ColorfulTab'
import MallList from '@/components/shared/MallList'
import Icon from '@/assets/icons'
import LinkCard from './components/LinkCard'
import SearchInput from '@/components/shared/SearchInput'
import Empty from '@/components/shared/Empty'
import HotSearchKeywordList from '@/components/shared/HotSearchKeywordList'
import LiveList from '@/components/shared/LiveList'
import Loading from '@/components/common/Loading'

// Constants
import { ROUTE_PATH } from '@/constants/routePath'
import { SEARCH_PRODUCT_FILTER_TAB_DATA, SEARCH_TYPE, searchTabsData } from '@/constants/search'
import { GTM_LOCATION, GtmItemListName } from '@/constants/gtmEvent'

// Styles
import styles from './style.module.scss'

const cx = classNames.bind(styles)
const SEARCH_SWIPER_COUNT = 5
const SEARCH_MOBILE_SWIPER_COUNT = 1.5
const SEARCH_TABLET_SWIPER_COUNT = 3

const SearchComponent = () => {
  const router = useRouter()
  const isTag = router.pathname === ROUTE_PATH.SEARCH_TAG
  const [searchTabActiveIndex, setSearchTabActiveIndex] = useState(
    router.query.type ? searchTabsData.findIndex((item) => item.value === router.query.type) : 0,
  )
  const [keyword, setKeyword] = useState(router.query.keyword || '')
  const [currentProductSort, setCurrentProductSort] = useState(SEARCH_PRODUCT_FILTER_TAB_DATA[0])

  // 固定於頁面頂端的搜尋區塊（search__content-header）採 position: fixed，
  // 因此需量測其實際高度，動態墊出下方內容的空間以符合 RWD（斷點 / Tab 有無時高度會變）
  const headerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)


  const { hotKeyData } = useSearchHotKey({})

  const { isDesktop, isTablet } = useMedia()
  const swiperCount = isDesktop ? SEARCH_SWIPER_COUNT : isTablet ? SEARCH_TABLET_SWIPER_COUNT : SEARCH_MOBILE_SWIPER_COUNT

  const { searchData, isSearchDataFetched, isSearchDataSuccess, productPaginationData } = useSearchData({
    keyword: keyword as string,
    productSort: currentProductSort.sort,
    isProductPagination: true,
    isTag: isTag,
  })
  const searchHotKeyData =
    !isUndefined(hotKeyData?.keywords) && !isNull(hotKeyData.keywords) ? hotKeyData?.keywords.map((item: any) => item.keyword) : []
  const { t } = useTranslation()

  const liveListData = {
    data: {
      pages: [{ roomList: searchData?.room }],
    },
    isFetchingNextPage: false,
    fetchNextPage: () => {},
    isFetched: isSearchDataFetched,
    isSuccess: isSearchDataSuccess,
  }

  const routerInitFunc = () => {
    if (router.isReady) {
      if (router.query.keyword) {
        setKeyword(router.query.keyword as string)
      }
      if (router.query.type) {
        setSearchTabActiveIndex(searchTabsData.findIndex((item) => item.value === router.query.type))
      }
    }
  }
  useEffect(() => {
    routerInitFunc()
  }, [router.isReady, router.query])

  useEffect(() => {
    const headerElement = headerRef.current
    if (!headerElement) return
    const updateHeaderHeight = () => setHeaderHeight(headerElement.offsetHeight)
    updateHeaderHeight()
    const resizeObserver = new ResizeObserver(updateHeaderHeight)
    resizeObserver.observe(headerElement)
    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    const headerElement = headerRef.current
    if (!headerElement) return
    const siteHeaderElement = document.querySelector('header')

    // 每次量測時「即時查詢」header 各區塊：header__bottom（mobile 導覽列）等區塊可能在
    // useMedia 於掛載後才切換到 mobile 時延後出現；若只在 effect 掛載時查一次，該參照會永遠是 null，
    // 造成量測遺漏 header__bottom（top 只算到 header__center 底部而被導覽列覆蓋）。
    const getHeaderParts = () => [
      document.querySelector('[class*="header__top"]'),
      document.querySelector('[class*="header__center"]'),
      document.querySelector('[class*="header__bottom"]'),
    ]
    // 取三區塊的最底緣；desktop 時 header__bottom 為 display:none（rect 全 0）不影響結果
    const measureOffset = () =>
      getHeaderParts().reduce((bottom, element) => {
        return element ? Math.max(bottom, element.getBoundingClientRect().bottom) : bottom
      }, 0)
    const isPinned = () => {
      const centerElement = document.querySelector('[class*="header__center"]')
      return !!centerElement && centerElement.className.includes('pin-to-top')
    }

    // 站台 header 以 transform + transition 0.2s linear 移動。若用 JS 逐幀量測去追，會因 transform
    // 由 compositor 執行而與主執行緒讀取的進度不同步 → 兩者速率快慢不一。
    // 改為讓本區塊也套用「相同的 CSS transition」：僅在狀態切換時把 top 設到目標靜止值，交由瀏覽器補間，
    // 速率／曲線與站台 header 完全一致。兩種靜止狀態（pinned / unpinned）的 offset 會被快取，
    // 讓切換的當下就能取得目標值、與站台同步啟動動畫。
    const restCache = new Map<boolean, number>()

    const setTop = (value: number, animate: boolean) => {
      headerElement.style.transition = animate ? 'top 0.2s linear' : 'none'
      headerElement.style.top = `${value}px`
    }
    const settle = () => {
      const offset = measureOffset()
      restCache.set(isPinned(), offset)
      setTop(offset, false)
    }

    let scheduleFrame = 0
    let chaseFrame = 0
    let animating = false
    let animateTimer = 0
    let prevPinned = isPinned()

    // 某方向第一次切換、該狀態尚無快取時，改用逐幀跟隨站台 header 實際位置（平滑、無跳位）；
    // 待這次動畫結束把最終值寫入快取，之後同方向切換即改用與站台等速的 CSS transition。
    const stopChase = () => {
      if (chaseFrame) {
        window.cancelAnimationFrame(chaseFrame)
        chaseFrame = 0
      }
    }
    const chase = () => {
      setTop(measureOffset(), false)
      chaseFrame = window.requestAnimationFrame(chase)
    }

    const scheduleSettle = () => {
      if (animating || scheduleFrame) return
      scheduleFrame = window.requestAnimationFrame(() => {
        scheduleFrame = 0
        settle()
      })
    }
    const endAnimation = () => {
      window.clearTimeout(animateTimer)
      stopChase()
      animating = false
      settle() // 校正到真實最終值並更新快取
    }
    const handleStateChange = () => {
      const pinned = isPinned()
      if (pinned === prevPinned) return // 只處理 pin-to-top 切換，忽略其他 class 變動
      prevPinned = pinned
      animating = true
      window.clearTimeout(animateTimer)
      animateTimer = window.setTimeout(endAnimation, 300) // transitionend 未觸發時的安全校正
      const target = restCache.get(pinned)
      // 已快取目標值 → 以與站台 header 相同的 0.2s linear 同步、等速動畫；
      // 尚無快取（該方向第一次切換）→ 逐幀跟隨站台 header，避免跳位
      if (target !== undefined) setTop(target, true)
      else if (!chaseFrame) chase()
    }

    settle() // 初始定位（不動畫）

    // 觀察整個站台 <header> 子樹：可捕捉 header__bottom 之類延後掛載的節點（childList），
    // 以及 pin-to-top / pin-to-center 等 class 切換（attributes），不受站台 scroll handler 的 1000ms throttle 影響
    const mutationObserver = new MutationObserver(() => {
      handleStateChange()
      scheduleSettle()
    })
    const resizeObserver = new ResizeObserver(scheduleSettle)
    if (siteHeaderElement) {
      mutationObserver.observe(siteHeaderElement, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      })
      resizeObserver.observe(siteHeaderElement)
    }

    siteHeaderElement?.addEventListener('transitionend', endAnimation)
    window.addEventListener('scroll', scheduleSettle, { passive: true })
    window.addEventListener('resize', scheduleSettle)

    return () => {
      if (scheduleFrame) window.cancelAnimationFrame(scheduleFrame)
      stopChase()
      window.clearTimeout(animateTimer)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      siteHeaderElement?.removeEventListener('transitionend', endAnimation)
      window.removeEventListener('scroll', scheduleSettle)
      window.removeEventListener('resize', scheduleSettle)
    }
  }, [])

  const displayEmpty = [...searchData.room, ...searchData.roomKol, ...searchData.merchant, ...searchData.product].length === 0 || isEmpty(keyword)

  const handleProductSeeMoreClick = () => {
    const activeIndex = searchTabsData.findIndex((item) => item.value === SEARCH_TYPE.PRODUCT)
    handleRoutePushWithQuery({ router, pathname: ROUTE_PATH.SEARCH, query: { keyword, type: SEARCH_TYPE.PRODUCT } })
    setSearchTabActiveIndex(activeIndex)
  }
  return (
    <div className={cx('search')}>
      <div className={cx('container')}>
        <div className={cx('search__content')} style={{ paddingTop: headerHeight ? `${headerHeight}px` : undefined }}>
          <div className={cx('search__content-header')} ref={headerRef}>
            <div className={cx('search__content-header-inner')}>
              <SearchInput />
              {isSearchDataSuccess && displayEmpty ? (
                <div className={cx('search__content-result-heading')}>
                  <div className={cx('search__content-result-text')}>{t('Search_Result_Text', { keyword })}</div>
                </div>
              ) : (
                isSearchDataFetched && (
                  <div className={cx('search__content-result-heading')}>
                    <div className={cx('search__content-result-text')}>{t('Search_Result_Text', { keyword })}</div>

                    <ColorfulTab
                      activeIndex={searchTabActiveIndex}
                      onChange={(value) => {
                        const activeIndex = searchTabsData.findIndex((item) => item.value === value)
                        handleRoutePushWithQuery({ router, pathname: isTag ? ROUTE_PATH.SEARCH_TAG : ROUTE_PATH.SEARCH, query: { keyword, type: value } })
                        setSearchTabActiveIndex(activeIndex)
                      }}
                      values={searchTabsData}
                    />
                  </div>
                )
              )}
            </div>
          </div>
          {(!isSearchDataFetched || !isSearchDataSuccess) && <Loading isFull={false} />}
          {isSearchDataSuccess && displayEmpty ? (
            <>
              <div className={cx('search__content-result__content')}>
                <Empty title={t('Search_Result_Empty_Title')} />
                <HotSearchKeywordList displayAmount={8} data={searchHotKeyData} />
              </div>
            </>
          ) : (
            <>
              {isSearchDataFetched && (
                <div className={cx('search__content-result')}>
                  <>
                    {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.PRODUCT && (
                      <div className={cx('search__content-result-product-filter')}>
                        {SEARCH_PRODUCT_FILTER_TAB_DATA.map((tabData) => (
                          <div
                            key={tabData.label}
                            className={cx('search__content-result-product-filter__tab', {
                              active: tabData === currentProductSort,
                            })}
                            onClick={() => {
                              setCurrentProductSort(tabData)
                            }}
                          >
                            {t(tabData.label)}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* 直播間搜尋結果 */}
                    <div className={cx('search__content-result__content')}>
                      {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.LIVE &&
                        searchData?.roomKol?.length < 1 &&
                        searchData?.room?.length < 1 && <Empty title={t('Search_Result_Empty_Title')} />}
                      {(searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.LIVE ||
                        searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.ALL) &&
                        searchData?.roomKol?.length > 0 && (
                          <div className={cx('search__content-result__content__row')}>
                            <div className={cx('search__content-result__content__row-header')}>
                              {<p>{t('Common_Host')}</p>}
                              {searchData?.roomKol?.length > swiperCount && (
                                <div
                                  onClick={() => {
                                    handleRoutePushWithQuery({ router, pathname: ROUTE_PATH.SEARCH_LIVE, query: { keyword, type: SEARCH_TYPE.LIVE } })
                                  }}
                                  className={cx('search__content-result__content__row-header-more')}
                                >
                                  <div>{t('Search_Result_See_More')}</div>
                                  <div className={cx('search__content-result__content__row-header-more-icon')}>
                                    <Icon.ChevronRight />
                                  </div>
                                </div>
                              )}
                            </div>
                            <Swiper
                              className={cx('search__content-result__content__row-cards')}
                              slidesPerView={swiperCount}
                              spaceBetween={10}
                              allowTouchMove={true}
                              grabCursor
                            >
                              {searchData?.roomKol?.map((data) => (
                                <SwiperSlide key={data.memberInfoId}>
                                  <LinkCard
                                    title={data.nickName}
                                    image={data.headshotUrl}
                                    handleClick={() => {
                                      handleRoutePushWithQuery({ router, pathname: `${ROUTE_PATH.USER}/${data.memberInfoId}`, query: { type: 0 } })
                                      handleGtmClickHost({
                                        location: GTM_LOCATION.SEARCH_RESULT,
                                        hostId: data.memberInfoId,
                                      })
                                    }}
                                  />
                                </SwiperSlide>
                              ))}
                              <div className={cx('search__content-result__content__row-cards-mask')} />
                            </Swiper>
                            {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.LIVE && (
                              <LiveList liveListData={liveListData} gtmLocation={GTM_LOCATION.SEARCH_RESULT} />
                            )}
                          </div>
                        )}
                      {/* 賣家搜尋結果 */}
                      {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.ALL && searchData?.merchant?.length > 0 && (
                        <div className={cx('search__content-result__content__row')}>
                          <div className={cx('search__content-result__content__row-header')}>
                            <p>{t('Search_Result_Header_Merchant')}</p>
                            {searchData?.merchant?.length > swiperCount && (
                              <div
                                onClick={() => {
                                  handleRoutePushWithQuery({
                                    router,
                                    pathname: ROUTE_PATH.SEARCH_MERCHANT,
                                    query: { keyword },
                                  })
                                }}
                                className={cx('search__content-result__content__row-header-more')}
                              >
                                <div>{t('Search_Result_See_More')}</div>
                                <div className={cx('search__content-result__content__row-header-more-icon')}>
                                  <Icon.ChevronRight />
                                </div>
                              </div>
                            )}
                          </div>
                          <Swiper
                            slidesPerView={swiperCount}
                            className={cx('search__content-result__content__row-cards')}
                            spaceBetween={10}
                            allowTouchMove={true}
                            grabCursor
                          >
                            {searchData?.merchant?.map((data) => (
                              <SwiperSlide key={data.merchantId}>
                                <LinkCard
                                  title={data.storeName}
                                  description={data.intro}
                                  image={data.headshotUrl}
                                  handleClick={() => {
                                    handleRoutePushWithQuery({ router, pathname: `${ROUTE_PATH.USER}/${data.memberInfoId}`, query: { type: 2 } })
                                    handleGtmClickMerchant({
                                      location: GTM_LOCATION.SEARCH_RESULT,
                                      merchantDetail: {
                                        merchantId: data.memberInfoId,
                                        merchantName: data.storeName,
                                      },
                                    })
                                  }}
                                />
                              </SwiperSlide>
                            ))}
                            <div className={cx('search__content-result__content__row-cards-mask')} />
                          </Swiper>
                        </div>
                      )}
                      {/* 商品搜尋結果 */}
                      {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.PRODUCT && searchData?.product?.length === 0 && (
                        <Empty title={t('Search_Result_Empty_Title')} />
                      )}

                      {(searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.PRODUCT ||
                        searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.ALL) &&
                        searchData?.product?.length > 0 && (
                          <div className={cx('search__content-result__content__row')}>
                            {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.ALL && (
                              <div className={cx('search__content-result__content__row-header')}>
                                <p>{t('Search_Result_Header_Product')}</p>
                                <div className={cx('search__content-result__content__row-header-more')} onClick={handleProductSeeMoreClick}>
                                  <div>{t('Search_Result_See_More')}</div>
                                  <div className={cx('search__content-result__content__row-header-more-icon')}>
                                    <Icon.ChevronRight />
                                  </div>
                                </div>
                              </div>
                            )}
                            <MallList
                              categoryListData={productPaginationData}
                              gtmItemEventParams={{
                                itemListName: `${GtmItemListName.SEARCH_RESULT}`,
                              }}
                            />
                          </div>
                        )}
                    </div>
                  </>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
export default SearchComponent
